import {
  accentTextOnCanvas,
  contrastRatio,
  darkPalettes,
  lightPalettes,
  mix,
  onFilled,
  withFocusRing,
  type BrandScale,
  type PaletteProps
} from 'pixelize-design-library';

import { buildBrandScale, buildRamp } from '@core/Theme/colorRamp';

type SemanticTone = keyof PaletteProps['semantic'];

/** Colours measured directly off the auth-pages reference design. */
const AUTH_BRAND_SEED = {
  canvas: '#080C11',
  canvasGradientFrom: '#0A1016',
  canvasGradientTo: '#05080C',
  canvasLight: '#EEF2F8',
  primary500: '#3a75cf',
  inputBorder: '#464b5d',
  gradientFrom: '#023869',
  gradientTo: '#001b3a'
};

const SEMANTIC_TONES: SemanticTone[] = ['success', 'error', 'warning', 'info'];

/** Resolved against the card surface, not bare canvas — every consuming page's ink sits on a card. */
function finalize(base: PaletteProps, primary: BrandScale, card: string): PaletteProps {
  const merged: PaletteProps = {
    ...base,
    primary,
    accentText: accentTextOnCanvas(primary, card),
    onPrimary: onFilled(primary[500], base.white, base.black),
    semanticText: Object.fromEntries(
      SEMANTIC_TONES.map((tone) => [tone, accentTextOnCanvas(base.semantic[tone], card)])
    ) as PaletteProps['semanticText'],
    onSemantic: Object.fromEntries(
      SEMANTIC_TONES.map((tone) => [
        tone,
        onFilled(base.semantic[tone][500], base.white, base.black)
      ])
    ) as PaletteProps['onSemantic']
  };
  return withFocusRing(merged);
}

const skylineDark = darkPalettes.skyline;
const skylineLight = lightPalettes.skyline;

/** Dark-mode ramps mirror light-mode's: low rungs go toward black, high rungs toward white. */
const darkPrimary = buildBrandScale(
  AUTH_BRAND_SEED.primary500,
  skylineDark.black,
  skylineDark.white
);
const darkCardSurface = mix(AUTH_BRAND_SEED.canvas, skylineDark.white, 0.03);
const darkTabListSurface = mix(AUTH_BRAND_SEED.canvas, skylineDark.white, 0.06);
/** The active-tab pill sits ON the tab list, so it must read lighter than `darkTabListSurface`. */
const darkTabActiveSurface = mix(AUTH_BRAND_SEED.canvas, skylineDark.white, 0.1);
const darkBorderRamp = buildRamp(
  AUTH_BRAND_SEED.inputBorder,
  AUTH_BRAND_SEED.canvas,
  skylineDark.white
);

export const authBrandDarkPalette: PaletteProps = finalize(
  {
    ...skylineDark,
    gradient: { from: AUTH_BRAND_SEED.gradientFrom, to: AUTH_BRAND_SEED.gradientTo },
    backgroundColor: {
      ...skylineDark.backgroundColor,
      main: AUTH_BRAND_SEED.canvas,
      light: darkCardSurface,
      secondary: darkTabListSurface,
      tertiary: darkTabActiveSurface,
      medium: AUTH_BRAND_SEED.canvasGradientFrom,
      quaternary: AUTH_BRAND_SEED.canvasGradientTo,
      base: AUTH_BRAND_SEED.canvas
    },
    background: { ...skylineDark.background, 50: AUTH_BRAND_SEED.canvas },
    boxborder: darkBorderRamp,
    border: darkBorderRamp
  },
  darkPrimary,
  darkCardSurface
);

/** The reference design ships dark-only — light mode carries the same accent onto `skyline`'s light surfaces. */
const lightPrimary = buildBrandScale(
  AUTH_BRAND_SEED.primary500,
  skylineLight.white,
  skylineLight.black
);

/** Darkens `hex` toward `towardHex` until it clears `min` contrast against `against` (WCAG 1.4.11). */
function ensureContrast(hex: string, against: string, min: number, towardHex: string): string {
  let candidate = hex;
  for (let weight = 0.05; contrastRatio(candidate, against) < min && weight <= 1; weight += 0.05) {
    candidate = mix(hex, towardHex, weight);
  }
  return candidate;
}

/** Stock `skylineLight.boxborder[500]` only reaches 2.29:1 on white — too low for WCAG 1.4.11's 3:1. */
const lightBorder500 = ensureContrast(
  skylineLight.boxborder[500],
  skylineLight.white,
  3,
  skylineLight.black
);
const lightBorderRamp = buildRamp(lightBorder500, skylineLight.white, skylineLight.black);

/** The branding panel is the reference design's fixed dark identity — it doesn't follow the light/dark toggle. */
export const authBrandLightPalette: PaletteProps = finalize(
  {
    ...skylineLight,
    gradient: { from: AUTH_BRAND_SEED.gradientFrom, to: AUTH_BRAND_SEED.gradientTo },
    backgroundColor: {
      // Stock skyline inverts elevation in light mode — keep the active-tab pill lighter than the tab list.
      ...skylineLight.backgroundColor,
      tertiary: skylineLight.backgroundColor.light,
      medium: AUTH_BRAND_SEED.canvasLight,
      quaternary: AUTH_BRAND_SEED.canvasLight
    },
    boxborder: lightBorderRamp,
    border: lightBorderRamp
  },
  lightPrimary,
  skylineLight.backgroundColor.light
);

export const authBrandPalette = { light: authBrandLightPalette, dark: authBrandDarkPalette };
